import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";

const MOTION_KEY = "play-admin.motion";
const HEADER_FX_KEY = "play-admin.season-fx-header";
const NAV_FX_KEY = "play-admin.season-fx-nav";

function prefersReducedMotion() {
	try {
		return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
	} catch {
		return false;
	}
}

function readFlag(key: string, fallback: boolean) {
	try {
		const stored = window.localStorage.getItem(key);
		if (stored === "0") {
			return false;
		}
		if (stored === "1") {
			return true;
		}
		return fallback;
	} catch {
		return fallback;
	}
}

function persistFlag(key: string, enabled: boolean) {
	try {
		window.localStorage.setItem(key, enabled ? "1" : "0");
	} catch {
		/* ignore quota / private mode */
	}
}

function motionFallback() {
	return !prefersReducedMotion();
}

type MotionContextValue = {
	motion: boolean;
	setMotion: (next: boolean) => void;
	headerFx: boolean;
	setHeaderFx: (next: boolean) => void;
	navFx: boolean;
	setNavFx: (next: boolean) => void;
};

const MotionContext = createContext<MotionContextValue | null>(null);

export function MotionProvider({ children }: { children: ReactNode }) {
	const fallback = motionFallback();
	const [motion, setMotionState] = useState(() =>
		readFlag(MOTION_KEY, fallback),
	);
	const [headerFx, setHeaderFxState] = useState(() =>
		readFlag(HEADER_FX_KEY, fallback),
	);
	const [navFx, setNavFxState] = useState(() => readFlag(NAV_FX_KEY, fallback));

	useEffect(() => {
		document.documentElement.dataset.motion = motion ? "on" : "off";
	}, [motion]);

	const value = useMemo<MotionContextValue>(
		() => ({
			motion,
			setMotion: (next: boolean) => {
				setMotionState(next);
				persistFlag(MOTION_KEY, next);
			},
			headerFx,
			setHeaderFx: (next: boolean) => {
				setHeaderFxState(next);
				persistFlag(HEADER_FX_KEY, next);
			},
			navFx,
			setNavFx: (next: boolean) => {
				setNavFxState(next);
				persistFlag(NAV_FX_KEY, next);
			},
		}),
		[headerFx, motion, navFx],
	);

	return (
		<MotionContext.Provider value={value}>{children}</MotionContext.Provider>
	);
}

export function useMotion(): MotionContextValue {
	const value = useContext(MotionContext);
	if (!value) {
		throw new Error("useMotion must be used inside MotionProvider");
	}
	return value;
}
